import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AlertTriangle, ChevronDown, Info, Plus, Trash2 } from "lucide-react";

import { useLocalization } from "@i18n";

import {
  useSaveInspectionMutation,
  type InspectionAttachment,
  type InspectionDetail,
  type InspectionItemResponseDto,
  type InspectionItemResponsePatch,
  type InspectionStatus,
} from "../../../api/inspection";
import {
  INSPECTION_OBSERVATIONS_LABEL_KEY,
  SPILLWAY_TYPE_OPTIONS,
} from "../inspection-form.catalog";
import { localizeInspectionStep } from "../localize-inspection-catalog";
import type {
  InspectionAnswerOption,
  InspectionEvidenceRule,
  InspectionFieldDef,
  InspectionItemDef,
  InspectionOccurrenceDef,
  InspectionRequiredRule,
  InspectionStepDef,
} from "../inspection-form.types";
import {
  gradeEvidenceOccurrenceKey,
  gradeObservationKey,
  isAnomalousGradeValue,
} from "../inspection-grade-evidence";
import InspectionPhotoEvidence from "./InspectionPhotoEvidence";
import { useBindInspectionStepSave, type BindInspectionStepSave } from "../../../utils/useBindInspectionStepSave";

// ---------------------------------------------------------------------------
// Estado local do step
// ---------------------------------------------------------------------------

interface ItemState {
  value?: string;
  fields: Record<string, string>;
  observations: string;
  photos: number;
}

interface ItemErrors {
  observations?: boolean;
  fields?: string[];
  answer?: boolean;
}

const EMPTY_ITEM_STATE: ItemState = {
  value: undefined,
  fields: {},
  observations: "",
  photos: 0,
};

/** Opções com rótulo longo (ex.: revestimento vegetal) vão em coluna. */
const RADIO_COL_LABEL_CHARS = 28;
const NA_VALUE = "notApplicable";

const INFO_HINT_KEYS = {
  optional: "inspection.form.hintOptional",
  informative: "inspection.form.hintInformative",
  informativeNoNa: "inspection.form.hintInformativeNoNa",
} as const;

const reqStar = (on: boolean): ReactNode =>
  on ? (
    <>
      {" "}
      <span className="insp-req" aria-hidden>
        *
      </span>
    </>
  ) : null;

const FIELD_ERROR_KEYS = {
  answer: "inspection.form.fieldErrorAnswer",
  observations: "inspection.form.fieldErrorObservations",
  photos: "inspection.form.fieldErrorPhotos",
  value: "inspection.form.fieldErrorValue",
} as const;

const radioGroupClass = (options?: { label: string }[]) =>
  (options ?? []).some((option) => option.label.length > RADIO_COL_LABEL_CHARS)
    ? "insp-radio-col"
    : "insp-radio-row";

const stateKey = (itemKey: string, occurrenceKey = "") =>
  `${itemKey}|${occurrenceKey}`;

const parseStateKey = (key: string): [string, string] => {
  const sep = key.indexOf("|");
  if (sep < 0) return [key, ""];
  return [key.slice(0, sep), key.slice(sep + 1)];
};

const optionOf = (item: InspectionItemDef, value?: string) =>
  item.options?.find((option) => option.value === value);

const isAnomalyOption = (option?: InspectionAnswerOption) =>
  Boolean(option?.anomaly);

const isGradeLikeOptions = (
  options?: { value: string; anomaly?: boolean }[],
) =>
  Boolean(
    options?.some((option) => option.value === "regular" && option.anomaly) &&
      options?.some((option) => option.value === "deficient" && option.anomaly),
  );

/** Nested grade items that own their own obs/fotos (estruturas complementares). */
const nestedOwnsAnomalyChrome = (item: InspectionItemDef) =>
  (item.nested ?? []).some(
    (nested) =>
      isGradeLikeOptions(nested.options) &&
      (nested.observations?.mode === "anomaly" ||
        nested.photos?.mode === "anomaly"),
  );

const gradeFieldKey = (item: InspectionItemDef) => {
  const segments = item.itemKey.split(".");
  return segments[segments.length - 1] ?? item.itemKey;
};

const isIndependentGradeField = (field: InspectionFieldDef) =>
  Boolean(
    field.subitem && field.type === "radio" && isGradeLikeOptions(field.options),
  );

const isRequired = (
  rule: InspectionRequiredRule | undefined,
  anomaly: boolean,
  hasValue = false,
) => {
  if (rule === "anomaly") return anomaly;
  if (rule === "whenValue") return hasValue;
  return Boolean(rule);
};

const showAnomalyPills = (options?: { label: string }[]) =>
  radioGroupClass(options) === "insp-radio-col";

const resolveEvidence = (
  rule: InspectionEvidenceRule | undefined,
  anomaly: boolean,
  value?: string,
): { visible: boolean; required: boolean } => {
  if (!rule || rule.mode === "anomaly") {
    return { visible: anomaly, required: anomaly };
  }

  if (rule.mode === "never") return { visible: false, required: false };

  if (rule.mode === "always") {
    return { visible: true, required: isRequired(rule.required, anomaly) };
  }

  const visible = value !== undefined && rule.values.includes(value);
  return {
    visible,
    required: visible && isRequired(rule.required, anomaly),
  };
};

const isFieldVisible = (field: InspectionFieldDef, value?: string) =>
  !field.when || (value !== undefined && field.when.includes(value));

const buildInitialState = (
  itemResponses: InspectionItemResponseDto[],
  attachments: InspectionAttachment[],
) => {
  const items: Record<string, ItemState> = {};
  const occurrences: Record<string, string[]> = {};

  for (const response of itemResponses) {
    const answer = (response.answerValue ?? {}) as Record<string, unknown>;
    const { value, ...rest } = answer;

    items[stateKey(response.itemKey, response.occurrenceKey)] = {
      value: typeof value === "string" ? value : undefined,
      fields: Object.fromEntries(
        Object.entries(rest).map(([key, fieldValue]) => [
          key,
          fieldValue === null || fieldValue === undefined
            ? ""
            : String(fieldValue),
        ]),
      ),
      observations: response.observations ?? "",
      photos: 0,
    };

    if (response.occurrenceKey) {
      const current = occurrences[response.itemKey] ?? [];
      if (!current.includes(response.occurrenceKey)) {
        occurrences[response.itemKey] = [...current, response.occurrenceKey];
      }
    }
  }

  for (const attachment of attachments) {
    const key = stateKey(attachment.itemKey, attachment.occurrenceKey);
    const current = items[key] ?? EMPTY_ITEM_STATE;
    items[key] = { ...current, photos: current.photos + 1 };
  }

  return { items, occurrences };
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface InspectionChecklistStepProps {
  step: InspectionStepDef;
  structureId: string;
  inspectionId: string;
  itemResponses: InspectionItemResponseDto[];
  canWrite: boolean;
  attachments?: InspectionAttachment[];
  inspectionStatus?: InspectionStatus;
  onSaved?: (detail: InspectionDetail) => void;
  onDetailSync?: (detail: InspectionDetail) => void;
  onBindSave?: BindInspectionStepSave;
}

const InspectionChecklistStep = ({
  step: stepTemplate,
  structureId,
  inspectionId,
  itemResponses,
  canWrite,
  attachments: attachmentsFromProps = [],
  inspectionStatus = "inProgress",
  onSaved,
  onDetailSync,
  onBindSave,
}: InspectionChecklistStepProps) => {
  const { translate } = useLocalization();

  // A cascata do Y=6 pode soft-deletar anexo sem o cliente pedir, e o 200 devolve a
  // ficha já podada (endpoints §7.2). Até o pai propagar o `detail` novo, esta
  // sobreposição é a verdade — senão o `InspectionPhotoEvidence` reporta a contagem
  // antiga e ressuscita a evidência órfã no estado local (form §7.7).
  const [syncedAttachments, setSyncedAttachments] = useState<
    InspectionAttachment[] | null
  >(null);
  const attachments = syncedAttachments ?? attachmentsFromProps;

  useEffect(() => {
    setSyncedAttachments(null);
  }, [attachmentsFromProps]);
  const step = useMemo(
    () => localizeInspectionStep(stepTemplate, translate),
    [stepTemplate, translate],
  );
  const initial = useMemo(
    () => buildInitialState(itemResponses, attachments),
    [itemResponses, attachments],
  );

  const [items, setItems] = useState<Record<string, ItemState>>(initial.items);
  const [occurrences, setOccurrences] = useState<Record<string, string[]>>(
    initial.occurrences,
  );
  const [removed, setRemoved] = useState<
    { itemKey: string; occurrenceKey: string }[]
  >([]);
  const [errors, setErrors] = useState<Record<string, ItemErrors>>({});
  const [isIncomplete, setIsIncomplete] = useState(false);
  const [errorFocusKey, setErrorFocusKey] = useState<string | null>(null);
  /**
   * Linhas alteradas nesta sessão. Resposta obrigatória (radio/campo) só é
   * cobrada de linha dirty — senão um PATCH de outra linha revalidaria o
   * checklist inteiro. Obs/foto de anomalia **sempre** revalidam: remover a
   * evidência depois do save não pode liberar o step (form §5.1 / §7.5).
   */
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [collapsedEntries, setCollapsedEntries] = useState<Record<string, boolean>>(
    {},
  );

  const { mutate: saveInspection } = useSaveInspectionMutation(
    structureId,
    inspectionId,
  );

  const isDirty = useCallback(
    (itemKey: string, occurrenceKey = "") => {
      const key = stateKey(itemKey, occurrenceKey);
      return touched.has(key) || !(key in initial.items);
    },
    [touched, initial.items],
  );

  const getState = useCallback(
    (itemKey: string, occurrenceKey = ""): ItemState =>
      items[stateKey(itemKey, occurrenceKey)] ?? EMPTY_ITEM_STATE,
    [items],
  );

  const patchState = useCallback(
    (itemKey: string, occurrenceKey: string, patch: Partial<ItemState>) => {
      setTouched((current) =>
        new Set(current).add(stateKey(itemKey, occurrenceKey)),
      );
      setItems((current) => {
        const key = stateKey(itemKey, occurrenceKey);
        return {
          ...current,
          [key]: { ...(current[key] ?? EMPTY_ITEM_STATE), ...patch },
        };
      });
    },
    [],
  );

  const setPhotoCount = useCallback(
    (itemKey: string, occurrenceKey: string, count: number) =>
      setItems((current) => {
        const key = stateKey(itemKey, occurrenceKey);
        const existing = current[key] ?? EMPTY_ITEM_STATE;
        if (existing.photos === count) return current;
        setTouched((keys) => new Set(keys).add(key));
        return { ...current, [key]: { ...existing, photos: count } };
      }),
    [],
  );

  /**
   * Troca de resposta da linha raiz. Quando a nova resposta deixa de admitir
   * observação, o texto é **descartado** na hora — o que está na tela é o que
   * está guardado, e o back apaga a obs órfã no mesmo PATCH (form §7.7).
   */
  const selectAnswer = (item: InspectionItemDef, next: string) => {
    const admitsObservations = resolveEvidence(
      item.observations,
      isAnomalyOption(optionOf(item, next)),
      next,
    ).visible;

    patchState(item.itemKey, "", {
      value: next,
      ...(admitsObservations ? {} : { observations: "" }),
    });
  };

  /** Idem para o grau da ocorrência, cuja obs vive em `fields[<grau>Observations]`. */
  const selectGrade = (
    item: InspectionItemDef,
    occurrenceKey: string,
    state: ItemState,
    gradeKey: string,
    next: string,
  ) => {
    const fields = { ...state.fields, [gradeKey]: next };
    if (!isAnomalousGradeValue(next)) delete fields[gradeObservationKey(gradeKey)];
    patchState(item.itemKey, occurrenceKey, { fields });
  };

  const collectGradeEvidenceErrors = (
    itemKey: string,
    occurrenceKey: string,
    state: ItemState,
    gradeKeys: string[],
    error: ItemErrors,
  ) => {
    for (const gradeKey of gradeKeys) {
      if (!isAnomalousGradeValue(state.fields[gradeKey])) continue;
      const obsKey = gradeObservationKey(gradeKey);
      if (!(state.fields[obsKey] ?? "").trim()) {
        error.fields = [...(error.fields ?? []), obsKey];
      }
      const photoOcc = gradeEvidenceOccurrenceKey(occurrenceKey, gradeKey);
      const photos = getState(itemKey, photoOcc).photos;
      const legacyPhotos = getState(itemKey, occurrenceKey).photos;
      if (photos === 0 && legacyPhotos === 0) {
        error.fields = [...(error.fields ?? []), `photos:${gradeKey}`];
      }
    }
  };

  // -------------------------------------------------------------------------
  // Visibilidade / anomalia
  // -------------------------------------------------------------------------

  const gateAnswer = step.gate ? getState(step.gate.itemKey).value : undefined;
  const stepApplies = !step.gate || gateAnswer !== NA_VALUE;

  const isItemVisible = useCallback(
    (item: InspectionItemDef) => {
      if (!item.visibleWhen) return true;
      const parent = getState(item.visibleWhen.itemKey).value;
      return parent !== undefined && item.visibleWhen.values.includes(parent);
    },
    [getState],
  );

  const itemAnomaly = useCallback(
    (item: InspectionItemDef, occurrenceKey = ""): boolean => {
      const state = getState(item.itemKey, occurrenceKey);

      if (isAnomalyOption(optionOf(item, state.value))) return true;

      // Graus embutidos no `answerValue` da raiz (ex.: drenagem superficial).
      for (const field of item.fields ?? []) {
        if (field.type !== "radio" || !isFieldVisible(field, state.value)) {
          continue;
        }
        const fieldValue = state.fields[field.key] ?? "";
        if (
          field.options?.some(
            (option) => option.value === fieldValue && option.anomaly,
          )
        ) {
          return true;
        }
      }

      if (
        item.nested &&
        state.value !== undefined &&
        (item.nestedWhen ?? []).includes(state.value)
      ) {
        return item.nested.some((nested) => itemAnomaly(nested));
      }

      return false;
    },
    [getState],
  );

  const occurrenceEnabled = (item: InspectionItemDef) => {
    if (!item.occurrences) return false;
    const value = getState(item.itemKey).value;
    return value !== undefined && item.occurrences.when.includes(value);
  };

  const activeOccurrenceKeys = (item: InspectionItemDef) =>
    (occurrences[item.itemKey] ?? []).filter(
      (key) =>
        !removed.some(
          (entry) =>
            entry.itemKey === item.itemKey && entry.occurrenceKey === key,
        ),
    );

  const rowAnomaly = (item: InspectionItemDef, occurrenceKey: string) => {
    const occurrence = item.occurrences;
    if (!occurrence) return false;
    if (occurrence.isAnomaly) return true;

    const state = getState(item.itemKey, occurrenceKey);
    return (occurrence.grades ?? []).some((grade) =>
      isAnomalyOption(optionOf(grade, state.fields[gradeFieldKey(grade)])),
    );
  };

  /** Chrome do card pai: ocorrência anômala (T/R/S/E/G ou grau Regular/Deficiente em L). */
  const itemHasAnomalousOccurrences = (item: InspectionItemDef) =>
    occurrenceEnabled(item) &&
    activeOccurrenceKeys(item).some((key) => rowAnomaly(item, key));

  /** `is-anomaly` do card — não altera exigência de obs/foto no pai (`itemAnomaly`). */
  const itemCardAnomaly = (item: InspectionItemDef) => {
    if (nestedOwnsAnomalyChrome(item)) {
      return isAnomalyOption(optionOf(item, getState(item.itemKey).value));
    }
    return itemAnomaly(item) || itemHasAnomalousOccurrences(item);
  };

  const addOccurrence = (item: InspectionItemDef) => {
    const occurrence = item.occurrences;
    if (!occurrence) return;

    setOccurrences((current) => {
      const existing = current[item.itemKey] ?? [];
      const nextIndex = existing.length + 1;
      return {
        ...current,
        [item.itemKey]: [...existing, `${occurrence.prefix}${nextIndex}`],
      };
    });
  };

  const removeOccurrence = (item: InspectionItemDef, occurrenceKey: string) =>
    setRemoved((current) => [
      ...current,
      { itemKey: item.itemKey, occurrenceKey },
    ]);

  // -------------------------------------------------------------------------
  // Serialização e validação
  // -------------------------------------------------------------------------

  const buildAnswerValue = (
    item: InspectionItemDef,
    state: ItemState,
    fields: InspectionFieldDef[],
  ) => {
    const answer: Record<string, unknown> = {};

    if (item.options) answer.value = state.value;

    for (const field of fields) {
      if (!isFieldVisible(field, state.value)) continue;
      const raw = state.fields[field.key] ?? "";
      if (raw === "") continue;
      answer[field.key] = raw;
      // Obs do grau só é admissível na faixa de anomalia: grau que voltou para Bom
      // não reenvia o texto antigo (form §7.7 variação 3).
      if (isIndependentGradeField(field) && isAnomalousGradeValue(raw)) {
        const obs = (state.fields[gradeObservationKey(field.key)] ?? "").trim();
        if (obs) answer[gradeObservationKey(field.key)] = obs;
      }
    }

    return answer;
  };

  const hasContent = (state: ItemState) =>
    state.value !== undefined ||
    state.observations.trim() !== "" ||
    Object.values(state.fields).some((value) => value !== "");

  const collectItem = (
    item: InspectionItemDef,
    rows: InspectionItemResponsePatch[],
    nextErrors: Record<string, ItemErrors>,
  ) => {
    const state = getState(item.itemKey);
    const dismissed =
      Boolean(optionOf(item, state.value)?.dismisses) ||
      Boolean(item.naCheckbox && state.value === NA_VALUE);
    const anomaly = itemAnomaly(item);
    const ownAnomaly = isAnomalyOption(optionOf(item, state.value));
    const fields = item.fields ?? [];
    const itemError: ItemErrors = {};

    if (!dismissed && isDirty(item.itemKey)) {
      if (
        item.options &&
        !item.optional &&
        state.value === undefined
      ) {
        itemError.answer = true;
      }

      for (const field of fields) {
        if (!isFieldVisible(field, state.value)) continue;
        if (
          isRequired(field.required, anomaly, Boolean(state.value)) &&
          !(state.fields[field.key] ?? "").trim()
        ) {
          itemError.fields = [...(itemError.fields ?? []), field.key];
        }
      }
    }

    const independentGradeFields = fields.filter(
      (field) =>
        isIndependentGradeField(field) && isFieldVisible(field, state.value),
    );

    if (!dismissed && independentGradeFields.length > 0) {
      collectGradeEvidenceErrors(
        item.itemKey,
        "",
        state,
        independentGradeFields.map((field) => field.key),
        itemError,
      );
    } else if (!dismissed) {
      const observations = resolveEvidence(
        item.observations,
        ownAnomaly,
        state.value,
      );
      const photos = resolveEvidence(item.photos, ownAnomaly, state.value);
      if (observations.required && !state.observations.trim()) {
        itemError.observations = true;
      }
      if (photos.required && state.photos === 0) {
        itemError.fields = [...(itemError.fields ?? []), "photos"];
      }
    }

    if (Object.keys(itemError).length > 0) {
      nextErrors[stateKey(item.itemKey)] = itemError;
    }

    if (hasContent(state)) {
      const naSelected = Boolean(item.naCheckbox && state.value === NA_VALUE);
      const answer = naSelected
        ? { value: NA_VALUE }
        : buildAnswerValue(item, state, fields);
      // Gate Sim/Não (ex.: `reservoir.bankCondition`): obs/foto vivem no nested
      // grade. Não reenviar texto antigo do pai no PATCH. Graus independentes
      // (canal) também: obs só nas chaves filhas do `answerValue`.
      //
      // `!admitsObservations` cobre a variação 3 da cascata (form §7.7): a resposta
      // saiu da faixa que admite obs, então o texto que sobrou no estado local não
      // pode voltar ao banco — o back apagaria em seguida de todo jeito.
      const omitObservations =
        item.observations?.mode === "never" ||
        nestedOwnsAnomalyChrome(item) ||
        independentGradeFields.length > 0 ||
        !resolveEvidence(item.observations, ownAnomaly, state.value).visible;
      rows.push({
        itemKey: item.itemKey,
        occurrenceKey: "",
        answerValue: answer,
        observations:
          naSelected || omitObservations ? null : state.observations.trim() || null,
      });
    }

    // Sub-itens do mesmo bloco (ex.: nota das margens, geomembrana).
    if (
      item.nested &&
      state.value !== undefined &&
      (item.nestedWhen ?? []).includes(state.value)
    ) {
      for (const nested of item.nested) collectItem(nested, rows, nextErrors);
    }

    // Ocorrências multi-instância.
    if (item.occurrences) {
      if (occurrenceEnabled(item)) {
        collectOccurrences(item, item.occurrences, rows, nextErrors);
      }
      // Fora do guard: remoção pedida pelo usuário vale mesmo que a raiz já tenha
      // saído do gate na mesma sessão — senão o PATCH não descreve o que ele fez
      // (form §7.7 / quality-test QT-INSP-07-85).
      collectRemovedOccurrences(item, item.occurrences, rows);
    }
  };

  const collectOccurrences = (
    item: InspectionItemDef,
    occurrence: InspectionOccurrenceDef,
    rows: InspectionItemResponsePatch[],
    nextErrors: Record<string, ItemErrors>,
  ) => {
    const targetItemKey = occurrence.itemKey ?? item.itemKey;
    const activeKeys = activeOccurrenceKeys(item);

    if (occurrence.requireAtLeastOne && activeKeys.length === 0) {
      nextErrors[stateKey(item.itemKey)] = {
        ...(nextErrors[stateKey(item.itemKey)] ?? {}),
        answer: true,
      };
    }

    for (const occurrenceKey of activeKeys) {
      const state = getState(item.itemKey, occurrenceKey);
      const rowError: ItemErrors = {};
      const answer: Record<string, unknown> = {};

      const rowAnomalyActive = rowAnomaly(item, occurrenceKey);
      const validateRow = isDirty(item.itemKey, occurrenceKey);

      for (const field of occurrence.fields) {
        const raw = (state.fields[field.key] ?? "").trim();
        if (validateRow && isRequired(field.required, rowAnomalyActive) && !raw) {
          rowError.fields = [...(rowError.fields ?? []), field.key];
        }
        if (raw !== "") {
          answer[field.key] = raw;
        }
      }

      const gradeKeys: string[] = [];
      for (const grade of occurrence.grades ?? []) {
        const key = gradeFieldKey(grade);
        gradeKeys.push(key);
        const value = state.fields[key] ?? "";
        if (validateRow && !value) {
          rowError.fields = [...(rowError.fields ?? []), key];
        } else {
          answer[key] = value;
        }
        if (!isAnomalousGradeValue(value)) continue;
        const obs = (state.fields[gradeObservationKey(key)] ?? "").trim();
        if (obs) answer[gradeObservationKey(key)] = obs;
      }

      if (gradeKeys.length > 0) {
        collectGradeEvidenceErrors(
          item.itemKey,
          occurrenceKey,
          state,
          gradeKeys,
          rowError,
        );
      } else {
        const observations = resolveEvidence(
          occurrence.observations,
          rowAnomalyActive,
        );
        if (observations.required && !state.observations.trim()) {
          rowError.observations = true;
        }

        const photos = resolveEvidence(occurrence.photos, rowAnomalyActive);
        if (photos.required && state.photos === 0) {
          rowError.fields = [...(rowError.fields ?? []), "photos"];
        }
      }

      if (Object.keys(rowError).length > 0) {
        nextErrors[stateKey(item.itemKey, occurrenceKey)] = rowError;
      }

      rows.push({
        itemKey: targetItemKey,
        occurrenceKey,
        answerValue: answer,
        observations:
          gradeKeys.length > 0 ? null : state.observations.trim() || null,
      });
    }

  };

  const collectRemovedOccurrences = (
    item: InspectionItemDef,
    occurrence: InspectionOccurrenceDef,
    rows: InspectionItemResponsePatch[],
  ) => {
    for (const entry of removed) {
      if (entry.itemKey !== item.itemKey) continue;
      rows.push({
        itemKey: occurrence.itemKey ?? item.itemKey,
        occurrenceKey: entry.occurrenceKey,
        remove: true,
      });
    }
  };

  const visibleItems = step.items.filter(isItemVisible);

  const handleSave = () => {
    const rows: InspectionItemResponsePatch[] = [];
    const nextErrors: Record<string, ItemErrors> = {};

    if (step.gate) {
      collectItem(step.gate, rows, nextErrors);
    }

    if (stepApplies) {
      for (const item of visibleItems) collectItem(item, rows, nextErrors);
    }

    setErrors(nextErrors);
    const errorKeys = Object.keys(nextErrors);
    setIsIncomplete(errorKeys.length > 0);

    const blocking = errorKeys.some((key) => {
      const error = nextErrors[key];
      if (!error) return false;
      if (error.observations || (error.fields && error.fields.length > 0)) {
        return true;
      }
      if (!error.answer) return false;
      if (!step.allowPartialSave) return true;
      const [itemKey, occurrenceKey] = parseStateKey(key);
      return getState(itemKey, occurrenceKey).value !== undefined;
    });

    if (errorKeys.length > 0) {
      setCollapsed({});
      setCollapsedEntries({});
      setErrorFocusKey(errorKeys[0] ?? null);
      if (blocking) return;
    } else {
      setErrorFocusKey(null);
    }

    saveInspection(
      { itemResponses: rows },
      {
        onSuccess: (detail) => {
          resyncFromDetail(detail);
          onSaved?.(detail);
        },
      },
    );
  };

  /**
   * O 200 do Y=6 devolve o estado **pós-cascata** (endpoints §7.2): linha que o back
   * soft-deletou tem de sair da tela sem recarregar a ficha, e evidência podada não
   * pode voltar num save seguinte.
   */
  const resyncFromDetail = (detail: InspectionDetail) => {
    const nextAttachments = detail.attachments ?? [];
    const synced = buildInitialState(detail.itemResponses ?? [], nextAttachments);
    setSyncedAttachments(nextAttachments);
    setItems(synced.items);
    setOccurrences(synced.occurrences);
    setRemoved([]);
    setTouched(new Set());
  };

  useBindInspectionStepSave(onBindSave, handleSave, canWrite);

  useEffect(() => {
    if (!errorFocusKey) return;
    const escaped =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(errorFocusKey)
        : errorFocusKey.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    const node = document.querySelector(`[data-error-key="${escaped}"]`);
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errorFocusKey]);

  // -------------------------------------------------------------------------
  // Renderização
  // -------------------------------------------------------------------------

  const renderRadioGroup = (
    item: InspectionItemDef,
    occurrenceKey: string,
    value: string | undefined,
    onSelect: (next: string) => void,
    disabled: boolean,
  ) => {
    const pills = showAnomalyPills(item.options);
    const missingAnswer = Boolean(errors[stateKey(item.itemKey, occurrenceKey)]?.answer);

    return (
    <fieldset
      role="radiogroup"
      aria-label={item.groupLabel ?? item.label}
      aria-invalid={missingAnswer ? "true" : undefined}
      className={`${radioGroupClass(item.options)}${missingAnswer ? " is-invalid" : ""}`}
    >
      {(item.options ?? []).map((option) => {
        const id = `${item.itemKey}-${occurrenceKey}-${option.value}`;
        const optionDisabled =
          disabled || (!option.dismisses && Boolean(optionOf(item, value)?.dismisses));

        return (
          <label key={option.value} className="insp-radio">
            <input
              id={id}
              type="radio"
              name={`${item.itemKey}-${occurrenceKey}`}
              value={option.value}
              checked={value === option.value}
              disabled={optionDisabled}
              onChange={() => onSelect(option.value)}
            />
            {option.label}
            {pills && option.anomaly ? (
              <span className="insp-tag-anom" aria-hidden>
                {translate("inspection.form.anomalyTag")}
              </span>
            ) : null}
          </label>
        );
      })}
      {missingAnswer ? (
        <p role="alert" className="insp-field-error">
          {translate(FIELD_ERROR_KEYS.answer)}
        </p>
      ) : null}
    </fieldset>
    );
  };

  const renderField = (
    item: InspectionItemDef,
    occurrenceKey: string,
    field: InspectionFieldDef,
    state: ItemState,
    disabled: boolean,
    invalid: boolean,
    required: boolean,
    footer?: ReactNode,
  ) => {
    const id = `${item.itemKey}-${occurrenceKey}-${field.key}`;
    const value = state.fields[field.key] ?? "";
    const onChange = (next: string) =>
      patchState(item.itemKey, occurrenceKey, {
        fields: { ...state.fields, [field.key]: next },
      });

    if (field.type === "radio") {
      const fieldAnomaly = Boolean(
        field.options?.some(
          (option) => option.value === value && option.anomaly,
        ),
      );
      const pills = showAnomalyPills(field.options);

      const radios = (
        <fieldset
          role="radiogroup"
          aria-label={field.label}
          aria-required={required || undefined}
          aria-invalid={invalid ? "true" : undefined}
          className={`${radioGroupClass(field.options)}${invalid ? " is-invalid" : ""}`}
        >
          {(field.options ?? []).map((option) => {
            const optionId = `${id}-${option.value}`;
            return (
              <label key={option.value} className="insp-radio">
                <input
                  id={optionId}
                  type="radio"
                  name={id}
                  value={option.value}
                  checked={value === option.value}
                  disabled={disabled}
                  onChange={() => onChange(option.value)}
                />
                {option.label}
                {pills && option.anomaly ? (
                  <span className="insp-tag-anom" aria-hidden>
                    {translate("inspection.form.anomalyTag")}
                  </span>
                ) : null}
              </label>
            );
          })}
          {invalid ? (
            <p role="alert" className="insp-field-error">
              {translate(FIELD_ERROR_KEYS.answer)}
            </p>
          ) : null}
        </fieldset>
      );

      if (field.subitem) {
        return (
          <div
            key={field.key}
            className={`insp-subitem${fieldAnomaly ? " is-anomaly" : ""}${
              invalid ? " is-invalid" : ""
            }`}
            data-error-key={invalid ? stateKey(item.itemKey, occurrenceKey) : undefined}
          >
            <div className="insp-subitem-title">{field.label}</div>
            {radios}
            {footer}
          </div>
        );
      }

      return (
        <div key={field.key}>
          <div className="insp-obs-lab">
            {field.label}
            {reqStar(required)}
          </div>
          {radios}
        </div>
      );
    }

    if (field.type === "select") {
      const options =
        field.options ??
        SPILLWAY_TYPE_OPTIONS.map((option) => ({
          value: option.value,
          label: translate(option.i18nKey),
        }));

      return (
        <div key={field.key} className="flex flex-col gap-1">
          <label htmlFor={id} className="insp-obs-lab">
            {field.label}
            {reqStar(required)}
          </label>
          <select
            id={id}
            value={value}
            required={required}
            disabled={disabled}
            aria-invalid={invalid ? "true" : undefined}
            onChange={(event) => onChange(event.target.value)}
            className="insp-inp"
          >
            <option value="">{translate("inspection.form.selectPlaceholder")}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {invalid ? (
            <p role="alert" className="insp-field-error">
              {translate(FIELD_ERROR_KEYS.value)}
            </p>
          ) : null}
        </div>
      );
    }

    return (
      <div key={field.key} className="flex flex-col gap-1">
        {field.label ? (
          <label htmlFor={id} className="insp-obs-lab">
            {field.label}
            {reqStar(required)}
          </label>
        ) : null}
        <input
          id={id}
          type="text"
          value={value}
          placeholder={field.placeholder}
          required={required}
          disabled={disabled}
          aria-label={field.label ? undefined : item.label}
          aria-invalid={invalid ? "true" : undefined}
          onChange={(event) => onChange(event.target.value)}
          className="insp-inp"
        />
        {invalid ? (
          <p role="alert" className="insp-field-error">
            {translate(FIELD_ERROR_KEYS.value)}
          </p>
        ) : null}
      </div>
    );
  };

  const renderObservations = (
    item: InspectionItemDef,
    occurrenceKey: string,
    state: ItemState,
    required: boolean,
    disabled: boolean,
    invalid: boolean,
  ) => {
    const id = `${item.itemKey}-${occurrenceKey}-observations`;

    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="insp-obs-lab">
          {translate(INSPECTION_OBSERVATIONS_LABEL_KEY)}
          {reqStar(required)}
        </label>
        <textarea
          id={id}
          value={state.observations}
          required={required}
          disabled={disabled}
          placeholder={
            item.observationsPlaceholder ??
            translate(
              item.itemKey === "spillway.type"
                ? "inspection.form.observationsPlaceholderOptional"
                : "inspection.form.observationsPlaceholder",
            )
          }
          aria-invalid={invalid ? "true" : undefined}
          onChange={(event) =>
            patchState(item.itemKey, occurrenceKey, {
              observations: event.target.value,
            })
          }
          className="insp-inp"
        />
        {invalid ? (
          <p role="alert" className="insp-field-error">
            {translate(FIELD_ERROR_KEYS.observations)}
          </p>
        ) : null}
      </div>
    );
  };

  const renderGradeObservations = (
    item: InspectionItemDef,
    occurrenceKey: string,
    gradeField: string,
    state: ItemState,
    required: boolean,
    disabled: boolean,
    invalid: boolean,
  ) => {
    const id = `${item.itemKey}-${occurrenceKey}-${gradeField}-observations`;
    const obsKey = gradeObservationKey(gradeField);
    const value = state.fields[obsKey] ?? "";

    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="insp-obs-lab">
          {translate(INSPECTION_OBSERVATIONS_LABEL_KEY)}
          {reqStar(required)}
        </label>
        <textarea
          id={id}
          value={value}
          required={required}
          disabled={disabled}
          placeholder={translate("inspection.form.observationsPlaceholder")}
          aria-invalid={invalid ? "true" : undefined}
          onChange={(event) =>
            patchState(item.itemKey, occurrenceKey, {
              fields: { ...state.fields, [obsKey]: event.target.value },
            })
          }
          className="insp-inp"
        />
        {invalid ? (
          <p role="alert" className="insp-field-error">
            {translate(FIELD_ERROR_KEYS.observations)}
          </p>
        ) : null}
      </div>
    );
  };

  const renderGradeEvidence = (
    item: InspectionItemDef,
    occurrenceKey: string,
    gradeField: string,
    state: ItemState,
    disabled: boolean,
    targetItemKey: string,
  ) => {
    const evidenceOcc = gradeEvidenceOccurrenceKey(occurrenceKey, gradeField);
    const rowErrors = errors[stateKey(item.itemKey, occurrenceKey)];
    const obsKey = gradeObservationKey(gradeField);

    return (
      <>
        {renderReqNote(true)}
        {renderGradeObservations(
          item,
          occurrenceKey,
          gradeField,
          state,
          true,
          disabled,
          Boolean(rowErrors?.fields?.includes(obsKey)),
        )}
        <InspectionPhotoEvidence
          structureId={structureId}
          inspectionId={inspectionId}
          itemKey={targetItemKey}
          occurrenceKey={evidenceOcc}
          canWrite={canWrite}
          inspectionStatus={inspectionStatus}
          admitsEvidence
          attachments={attachments.filter(
            (attachment) =>
              attachment.itemKey === targetItemKey &&
              attachment.occurrenceKey === evidenceOcc,
          )}
          anomalyActive
          invalid={Boolean(rowErrors?.fields?.includes(`photos:${gradeField}`))}
          disabled={disabled}
          onCountChange={(count) =>
            setPhotoCount(item.itemKey, evidenceOcc, count)
          }
          onDetailSync={onDetailSync}
        />
      </>
    );
  };

  const renderReqNote = (gradeLike: boolean) => (
    <p className="insp-req-note">
      <AlertTriangle aria-hidden size={14} />
      {translate(
        gradeLike
          ? "inspection.form.anomalyReqNoteGrade"
          : "inspection.form.anomalyReqNote",
      )}
    </p>
  );

  const renderGradeSubBlock = (
    grade: InspectionItemDef,
    parent: InspectionItemDef,
    occurrenceKey: string,
    state: ItemState,
    disabled: boolean,
    extras?: { showNote?: boolean; evidence?: ReactNode },
  ) => {
    const key = gradeFieldKey(grade);
    const value = state.fields[key] ?? "";
    const anomaly = isAnomalyOption(optionOf(grade, value));

    return (
      <div
        key={grade.itemKey}
        data-testid="inspection-item-block"
        data-anomaly={String(anomaly)}
        className={`insp-subitem${anomaly ? " is-anomaly" : ""}`}
      >
        <div className="insp-subitem-title">{grade.label}</div>
        <fieldset
          role="radiogroup"
          aria-label={grade.label}
          className={radioGroupClass(grade.options)}
        >
          {(grade.options ?? []).map((option) => {
            const id = `${parent.itemKey}-${occurrenceKey}-${key}-${option.value}`;
            return (
              <label key={option.value} className="insp-radio">
                <input
                  id={id}
                  type="radio"
                  name={`${parent.itemKey}-${occurrenceKey}-${key}`}
                  value={option.value}
                  checked={value === option.value}
                  disabled={disabled}
                  onChange={() =>
                    selectGrade(parent, occurrenceKey, state, key, option.value)
                  }
                />
                {option.label}
              </label>
            );
          })}
        </fieldset>
        {anomaly && extras?.showNote !== false ? renderReqNote(true) : null}
        {extras?.evidence}
      </div>
    );
  };

  const renderOccurrences = (item: InspectionItemDef) => {
    const occurrence = item.occurrences;
    if (!occurrence || !occurrenceEnabled(item)) return null;

    return (
      <div className="mt-2 flex flex-col gap-3">
        {activeOccurrenceKeys(item).length === 0 && occurrence.requireAtLeastOne ? (
          <div className="insp-empty-inline">
            <Info aria-hidden size={14} />
            {translate(
              occurrence.emptyMessageKey ?? "inspection.form.occurrenceEmpty",
            )}
          </div>
        ) : null}

        {activeOccurrenceKeys(item).map((occurrenceKey, occurrenceIndex) => {
          const state = getState(item.itemKey, occurrenceKey);
          const anomaly = rowAnomaly(item, occurrenceKey);
          const rowErrors = errors[stateKey(item.itemKey, occurrenceKey)];
          const observations = resolveEvidence(occurrence.observations, anomaly);
          const photos = resolveEvidence(occurrence.photos, anomaly);
          const entryKey = stateKey(item.itemKey, occurrenceKey);
          const isEntryCollapsed = Boolean(collapsedEntries[entryKey]);
          const tagFieldKeys = new Set(occurrence.tagFields ?? []);
          const fieldSnippets = occurrence.fields
            .filter((field) => !tagFieldKeys.has(field.key))
            .map((field) => {
              const raw = state.fields[field.key] ?? "";
              if (!raw) return "";
              if (field.type === "radio" || field.type === "select") {
                return (
                  field.options?.find((option) => option.value === raw)?.label ??
                  raw
                );
              }
              return raw;
            })
            .filter(Boolean);
          const gradeSnippets = (occurrence.grades ?? []).flatMap((grade) => {
            const raw = state.fields[gradeFieldKey(grade)] ?? "";
            const option = optionOf(grade, raw);
            if (!option) return [];
            const prefix = grade.itemKey.endsWith("cleaningState")
              ? translate("inspection.form.collapseCleanliness")
              : grade.itemKey.endsWith("structuralCondition")
                ? translate("inspection.form.collapseStructure")
                : grade.label;
            return [`${prefix}: ${option.label}`];
          });
          const snippets = [...fieldSnippets, ...gradeSnippets].slice(0, 3);
          const tagFieldParts = (occurrence.tagFields ?? [])
            .map((key) => {
              const field = occurrence.fields.find((entry) => entry.key === key);
              const raw = state.fields[key] ?? "";
              if (!raw || !field) return "";
              if (field.type === "select" || field.type === "radio") {
                return (
                  field.options?.find((option) => option.value === raw)?.label ??
                  raw
                );
              }
              return raw;
            })
            .filter(Boolean);
          const tag =
            occurrence.entryTag === "fields"
              ? tagFieldParts.length
                ? tagFieldParts.join(" · ")
                : translate("inspection.form.occurrencePendingTag")
              : occurrence.entryTag === "line"
                ? translate("inspection.form.entryLineTag", {
                    n: occurrenceIndex + 1,
                  })
                : occurrence.showKey
                  ? occurrenceKey
                  : "";
          const entrySummary = snippets.length
            ? [tag, ...snippets].filter(Boolean).join(" · ")
            : tag || translate("inspection.form.pendingSummary");
          const anomalousGrades = (occurrence.grades ?? []).filter((grade) =>
            isAnomalyOption(
              optionOf(grade, state.fields[gradeFieldKey(grade)]),
            ),
          );
          const embedEvidenceInGrades = (occurrence.grades ?? []).length > 0;
          const occurrenceEvidence = (
            <>
              {observations.visible
                ? renderObservations(
                    item,
                    occurrenceKey,
                    state,
                    observations.required,
                    !canWrite,
                    Boolean(rowErrors?.observations),
                  )
                : null}
              {photos.visible ? (
                <InspectionPhotoEvidence
                  structureId={structureId}
                  inspectionId={inspectionId}
                  itemKey={occurrence.itemKey ?? item.itemKey}
                  occurrenceKey={occurrenceKey}
                  canWrite={canWrite}
                  inspectionStatus={inspectionStatus}
                  admitsEvidence
                  attachments={attachments.filter(
                    (attachment) =>
                      attachment.itemKey ===
                        (occurrence.itemKey ?? item.itemKey) &&
                      attachment.occurrenceKey === occurrenceKey,
                  )}
                  anomalyActive={photos.required}
                  invalid={Boolean(rowErrors?.fields?.includes("photos"))}
                  onCountChange={(count) =>
                    setPhotoCount(item.itemKey, occurrenceKey, count)
                  }
                  onDetailSync={onDetailSync}
                />
              ) : null}
            </>
          );

          return (
            <div
              key={occurrenceKey}
              data-testid={occurrence.rowTestId}
              data-anomaly={String(anomaly)}
              data-error-key={entryKey}
              className={`insp-entry-card${anomaly ? " is-anomaly" : ""}${
                rowErrors ? " is-invalid" : ""
              }${isEntryCollapsed ? " is-collapsed" : ""}`}
            >
              <div className="insp-entry-head">
                <div className="insp-entry-head-left">
                  <button
                    type="button"
                    className="insp-collapse-btn"
                    aria-label={
                      isEntryCollapsed
                        ? translate("inspection.form.collapseExpand")
                        : translate("inspection.form.collapseMinimize")
                    }
                    onClick={() =>
                      setCollapsedEntries((current) => ({
                        ...current,
                        [entryKey]: !current[entryKey],
                      }))
                    }
                  >
                    <ChevronDown
                      aria-hidden
                      size={16}
                      style={{
                        transform: isEntryCollapsed ? "rotate(-90deg)" : "none",
                      }}
                    />
                  </button>
                  {isEntryCollapsed ? (
                    <span className="insp-collapse-summary">{entrySummary}</span>
                  ) : null}
                  {tag ? (
                    <span className="insp-entry-tag">{tag}</span>
                  ) : null}
                </div>
                <div className="insp-entry-head-actions">
                  {canWrite ? (
                    <button
                      type="button"
                      className="insp-btn insp-btn-ghost insp-btn-sm"
                      onClick={() => removeOccurrence(item, occurrenceKey)}
                    >
                      <Trash2 aria-hidden size={16} />
                      {translate("inspection.form.removeOccurrence")}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="insp-entry-body">
                {occurrence.fields.map((field) =>
                  renderField(
                    item,
                    occurrenceKey,
                    field,
                    state,
                    !canWrite,
                    Boolean(rowErrors?.fields?.includes(field.key)),
                    isRequired(field.required, anomaly, Boolean(state.value)),
                  ),
                )}

                {(occurrence.grades ?? []).map((grade) => {
                  const key = gradeFieldKey(grade);
                  const gradeAnomaly = isAnomalyOption(
                    optionOf(grade, state.fields[key]),
                  );
                  return renderGradeSubBlock(
                    grade,
                    item,
                    occurrenceKey,
                    state,
                    !canWrite,
                    {
                      showNote: false,
                      evidence:
                        embedEvidenceInGrades && gradeAnomaly
                          ? renderGradeEvidence(
                              item,
                              occurrenceKey,
                              key,
                              state,
                              !canWrite,
                              occurrence.itemKey ?? item.itemKey,
                            )
                          : null,
                    },
                  );
                })}

                {anomalousGrades.length >= 2 ? (
                  <p className="insp-hint">
                    {translate(
                      "inspection.form.drainageChannelDoubleAnomalyHint",
                    )}
                  </p>
                ) : null}

                {embedEvidenceInGrades ? null : occurrenceEvidence}

                {occurrence.isAnomaly ? (
                  <div className="insp-entry-anom-note">
                    <AlertTriangle aria-hidden size={14} />
                    {translate(
                      occurrence.prefix === "G"
                        ? "inspection.form.entryAnomNoteGeomembrane"
                        : "inspection.form.entryAnomNote",
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}

        {canWrite ? (
          <button
            type="button"
            className="insp-btn insp-btn-outline insp-btn-sm self-start"
            onClick={() => addOccurrence(item)}
          >
            <Plus aria-hidden size={14} />
            {occurrence.addLabel}
          </button>
        ) : null}
      </div>
    );
  };

  const renderItemBody = (item: InspectionItemDef) => {
    const state = getState(item.itemKey);
    const naChecked = Boolean(item.naCheckbox && state.value === NA_VALUE);
    const anomaly = itemAnomaly(item);
    const ownAnomaly = isAnomalyOption(optionOf(item, state.value));
    const dismissed =
      Boolean(optionOf(item, state.value)?.dismisses) || naChecked;
    const itemErrors = errors[stateKey(item.itemKey)];
    const observations = resolveEvidence(
      item.observations,
      ownAnomaly,
      state.value,
    );
    const photos = resolveEvidence(item.photos, ownAnomaly, state.value);
    const nestedVisible =
      item.nested &&
      state.value !== undefined &&
      (item.nestedWhen ?? []).includes(state.value);
    const radioItem = item.naCheckbox
      ? {
          ...item,
          options: (item.options ?? []).filter(
            (option) => option.value !== NA_VALUE,
          ),
        }
      : item;
    const gradeSubFields = (item.fields ?? []).filter(
      (field) =>
        field.subitem &&
        field.type === "radio" &&
        isGradeLikeOptions(field.options),
    );
    const embedEvidenceInGradeFields = gradeSubFields.length > 0;
    const nestedOwnsOwnEvidence = (item.nested ?? []).some(
      (nested) =>
        nested.observations?.mode === "anomaly" ||
        nested.photos?.mode === "anomaly",
    );
    const nestedHoldsGradeEvidence =
      Boolean(nestedVisible) &&
      (item.nested ?? []).some((nested) =>
        isGradeLikeOptions(nested.options),
      ) &&
      (item.observations?.mode === "anomaly" ||
        item.photos?.mode === "anomaly") &&
      !nestedOwnsOwnEvidence;
    const hideTopLevelEvidence =
      embedEvidenceInGradeFields ||
      nestedHoldsGradeEvidence ||
      nestedOwnsAnomalyChrome(item);
    const itemEvidence = (
      <>
        {observations.visible
          ? renderObservations(
              item,
              "",
              state,
              observations.required,
              !canWrite || dismissed,
              Boolean(itemErrors?.observations),
            )
          : null}
        {photos.visible ? (
          <InspectionPhotoEvidence
            structureId={structureId}
            inspectionId={inspectionId}
            itemKey={item.itemKey}
            canWrite={canWrite}
            inspectionStatus={inspectionStatus}
            admitsEvidence={item.admitsEvidence ?? true}
            attachments={attachments.filter(
              (attachment) =>
                attachment.itemKey === item.itemKey &&
                attachment.occurrenceKey === "",
            )}
            anomalyActive={photos.required}
            invalid={Boolean(itemErrors?.fields?.includes("photos"))}
            disabled={dismissed}
            onCountChange={(count) => setPhotoCount(item.itemKey, "", count)}
            onDetailSync={onDetailSync}
          />
        ) : null}
      </>
    );

    return (
      <>
        {item.naCheckbox ? (
          <label className="insp-chk-na">
            <input
              type="checkbox"
              checked={naChecked}
              disabled={!canWrite}
              onChange={(event) =>
                patchState(item.itemKey, "", {
                  value: event.target.checked ? NA_VALUE : undefined,
                  fields: event.target.checked ? {} : state.fields,
                  observations: event.target.checked ? "" : state.observations,
                })
              }
            />
            {translate("inspection.form.naCheckbox")}
          </label>
        ) : null}

        {naChecked ? null : (
          <>
            {radioItem.options
              ? renderRadioGroup(
                  radioItem,
                  "",
                  state.value,
                  (next) => selectAnswer(item, next),
                  !canWrite,
                )
              : null}

            {(item.fields ?? [])
              .filter((field) => isFieldVisible(field, state.value))
              .map((field) =>
                renderField(
                  item,
                  "",
                  field,
                  state,
                  !canWrite || dismissed,
                  Boolean(itemErrors?.fields?.includes(field.key)),
                  isRequired(field.required, anomaly, Boolean(state.value)),
                  embedEvidenceInGradeFields &&
                  field.options?.some(
                    (option) =>
                      option.anomaly &&
                      option.value === (state.fields[field.key] ?? ""),
                  )
                    ? renderGradeEvidence(
                        item,
                        "",
                        field.key,
                        state,
                        !canWrite || dismissed,
                        item.itemKey,
                      )
                    : undefined,
                ),
              )}

            {anomaly &&
            !hideTopLevelEvidence &&
            !nestedOwnsAnomalyChrome(item)
              ? renderReqNote(isGradeLikeOptions(item.options))
              : null}

            {(item.infoHints ?? [])
              .filter((hint) => hint === "informativeNoNa")
              .map((hint) => (
                <p key={hint} className="insp-hint">
                  {translate(INFO_HINT_KEYS[hint])}
                </p>
              ))}

            {nestedVisible
              ? item.nested?.map((nested) =>
                  renderNestedItem(
                    nested,
                    nestedHoldsGradeEvidence ? itemEvidence : null,
                  ),
                )
              : null}

            {hideTopLevelEvidence ? null : itemEvidence}

            {(item.infoHints ?? [])
              .filter((hint) => hint !== "informativeNoNa")
              .map((hint) => (
                <p key={hint} className="insp-hint">
                  {translate(INFO_HINT_KEYS[hint])}
                </p>
              ))}

            {renderOccurrences(item)}
          </>
        )}
      </>
    );
  };

  function renderNestedItem(item: InspectionItemDef, afterBody?: ReactNode) {
    const anomaly = itemAnomaly(item);

    return (
      <div
        key={item.itemKey}
        data-testid="inspection-item-block"
        data-anomaly={String(anomaly)}
        data-error-key={stateKey(item.itemKey)}
        className={`insp-subitem${anomaly ? " is-anomaly" : ""}${
          errors[stateKey(item.itemKey)] ? " is-invalid" : ""
        }`}
      >
        <div className="insp-subitem-title">{item.label}</div>
        {renderItemBody(item)}
        {afterBody}
      </div>
    );
  }

  const itemCollapseSummary = (item: InspectionItemDef) => {
    const state = getState(item.itemKey);
    const selectedOption = optionOf(item, state.value);

    if (item.occurrences && occurrenceEnabled(item)) {
      const count = activeOccurrenceKeys(item).length;
      if (count > 0) {
        return translate(
          item.occurrences.prefix === "L"
            ? "inspection.form.collapseLineCount"
            : "inspection.form.collapseRecordCount",
          { count },
        );
      }
      return translate("inspection.form.collapseYesPending");
    }

    if (
      item.nested?.length === 1 &&
      !item.nested[0]?.naCheckbox &&
      state.value !== undefined &&
      (item.nestedWhen ?? []).includes(state.value)
    ) {
      const nested = item.nested[0];
      const nestedOption = optionOf(nested, getState(nested.itemKey).value);
      if (nestedOption) return nestedOption.label;
      if (state.value === "yes") {
        return translate("inspection.form.collapseYesPending");
      }
    }

    if (selectedOption) return selectedOption.label;

    for (const field of item.fields ?? []) {
      if (field.type !== "select") continue;
      const raw = state.fields[field.key] ?? "";
      if (!raw) continue;
      const options =
        field.options ??
        SPILLWAY_TYPE_OPTIONS.map((option) => ({
          value: option.value,
          label: translate(option.i18nKey),
        }));
      return options.find((option) => option.value === raw)?.label ?? raw;
    }

    const filledText = (item.fields ?? [])
      .map((field) => (state.fields[field.key] ?? "").trim())
      .find((value) => value !== "");
    if (filledText) return filledText;
    if (state.observations.trim()) return state.observations.trim();

    if (item.optional || item.descriptive) {
      return translate("inspection.form.optionalSummary");
    }

    return translate("inspection.form.pendingSummary");
  };

  const renderItem = (item: InspectionItemDef) => {
    const anomaly = itemCardAnomaly(item);
    const isCollapsed = Boolean(collapsed[item.itemKey]);
    const summary = itemCollapseSummary(item);

    return (
      <Fragment key={item.itemKey}>
        {item.sectionTitle ? (
          <h4 className="insp-form-group-title">{item.sectionTitle}</h4>
        ) : null}
        <div
          data-testid="inspection-item-block"
          data-anomaly={String(anomaly)}
          data-error-key={stateKey(item.itemKey)}
          className={`insp-item-card${anomaly ? " is-anomaly" : ""}${
            errors[stateKey(item.itemKey)] ? " is-invalid" : ""
          }${isCollapsed ? " is-collapsed" : ""}`}
        >
          <div className="insp-item-card-head">
            <h4 className="insp-item-title">{item.label}</h4>
            <div className="insp-card-head-right">
              <button
                type="button"
                className="insp-collapse-btn"
                aria-label={
                  isCollapsed
                    ? translate("inspection.form.collapseExpand")
                    : translate("inspection.form.collapseMinimize")
                }
                onClick={() =>
                  setCollapsed((current) => ({
                    ...current,
                    [item.itemKey]: !current[item.itemKey],
                  }))
                }
              >
                <ChevronDown
                  aria-hidden
                  size={16}
                  style={{
                    transform: isCollapsed ? "rotate(-90deg)" : "none",
                  }}
                />
              </button>
              {isCollapsed ? (
                <span className="insp-collapse-summary">{summary}</span>
              ) : null}
            </div>
          </div>
          <div className="insp-card-body">{renderItemBody(item)}</div>
        </div>
      </Fragment>
    );
  };

  const renderGate = (gate: InspectionItemDef) => {
    const state = getState(gate.itemKey);
    const question = translate("inspection.form.stepAppliesQuestion");

    return (
      <div className="insp-gate-box">
        <div className="insp-gate-q">{question}</div>
        {renderRadioGroup(
          { ...gate, groupLabel: question },
          "",
          state.value,
          (next) => patchState(gate.itemKey, "", { value: next }),
          !canWrite,
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {isIncomplete ? (
        <p role="alert" className="insp-step-error-banner">
          <AlertTriangle aria-hidden size={16} />
          {translate("inspection.form.stepIncomplete")}
        </p>
      ) : null}

      {step.hint ? (
        <p className="text-sm text-[var(--insp-text)]">{step.hint}</p>
      ) : null}

      {step.gate ? renderGate(step.gate) : null}

      {stepApplies ? (
        visibleItems.map((item) => renderItem(item))
      ) : (
        <div className="insp-empty-box">
          <Info aria-hidden className="insp-empty-box-icon" />
          {translate("inspection.form.stepNotApplicable")}
        </div>
      )}

      {canWrite && !onBindSave ? (
        <button
          type="button"
          className="insp-btn insp-btn-primary self-end"
          onClick={handleSave}
        >
          {translate("inspection.form.saveAndContinue")}
        </button>
      ) : null}
    </div>
  );
};

export default InspectionChecklistStep;
